/**
 * feat-307-redesign-tenant-mgmt (spec D2, B2) — the left sidebar tree.
 *
 * Tenants the role can see, with a filter box. Only the SELECTED tenant expands
 * to its locations (lazy: they load through the same `fetchTenantDetails` the
 * old "Show Details" used). The current node is highlighted and carries
 * `aria-current="page"`. Every node is a real link, so a node is also a deep
 * link (open in new tab, copy link) and browser back/forward walks the levels.
 *
 * D4 / F1: a tenant's location count is printed ONLY once its locations have
 * loaded. GET /api/tenant carries no count, so an unloaded tenant shows
 * nothing rather than a misleading 0. No crew counts anywhere (F4).
 *
 * Live dot and the Live / Drip on filter chips are Admin-only: they expose the
 * same flags as the Admin-only Live client / Drip release controls, so they
 * take the same fail-closed `showClientStatus` the caller computes.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, LayoutGrid, MapPin, Plus, Search } from './icons';
import { Tenant } from '../../../types/tenant';
import { cls } from './styles';
import { locationPath, rootPath, tenantPath } from './routes';

type Chip = 'all' | 'live' | 'drip';

export interface TenantTreeProps {
  tenants: Tenant[];
  selectedTenantId: string | null;
  selectedLocationId: string | null;
  loadedTenants: Record<string, boolean>;
  loadingTenants: Record<string, boolean>;
  /** Admin root view: the "All tenants" node and the filter box. */
  showRoot: boolean;
  /** Fail-closed Admin gate (`isAdminForLiveClient`). */
  showClientStatus: boolean;
  canCreateLocation: (tenantId: string) => boolean;
  onAddLocation: (tenant: Tenant) => void;
  /** Called after any node is followed (closes the narrow-viewport drawer). */
  onNavigate?: () => void;
}

export default function TenantTree({
  tenants,
  selectedTenantId,
  selectedLocationId,
  loadedTenants,
  loadingTenants,
  showRoot,
  showClientStatus,
  canCreateLocation,
  onAddLocation,
  onNavigate,
}: TenantTreeProps) {
  const [query, setQuery] = useState('');
  const [chip, setChip] = useState<Chip>('all');

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tenants.filter((t) => {
      if (showClientStatus && chip === 'live' && !t.isLive) return false;
      if (showClientStatus && chip === 'drip' && !t.dripEnabled) return false;
      if (!q) return true;
      if (t.tenantName.toLowerCase().includes(q)) return true;
      // Only locations that are already loaded can match — no fan-out fetch.
      return t.locations.some((l) => l.name.toLowerCase().includes(q));
    });
  }, [tenants, query, chip, showClientStatus]);

  const rootActive = !selectedTenantId;
  const chipBtn = (key: Chip, label: string) => (
    <button
      key={key}
      type="button"
      aria-pressed={chip === key}
      onClick={() => setChip(key)}
      className={`text-xs font-bold rounded-full px-2 py-0.5 ${
        chip === key ? 'bg-navActiveBg text-navActive' : 'text-inkMuted hover:bg-cream-dark'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-col h-full" data-testid="tenant-tree">
      {showRoot && (
        <div className="p-3 border-b border-softBorder">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-inkMuted" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={`${cls.input} pl-8 !py-1.5`}
              placeholder={`Filter ${tenants.length} tenants and their locations`}
              aria-label="Filter tenants"
              data-testid="tenant-tree-filter"
            />
          </div>
          {showClientStatus && (
            <div className="flex gap-1 mt-2" role="group" aria-label="Show">
              {chipBtn('all', 'All')}
              {chipBtn('live', 'Live')}
              {chipBtn('drip', 'Drip on')}
            </div>
          )}
        </div>
      )}
      <nav className="flex-1 overflow-y-auto py-2 text-sm" aria-label="Tenants">
        {showRoot && (
          <Link
            to={rootPath()}
            onClick={onNavigate}
            aria-current={rootActive ? 'page' : undefined}
            data-testid="tree-root"
            className={`flex items-center gap-2 mx-2 px-2 py-1.5 rounded-lg no-underline ${
              rootActive ? 'bg-navActiveBg text-navActive font-bold' : 'text-ink hover:bg-cream-dark font-semibold'
            }`}
          >
            <LayoutGrid className="w-4 h-4" aria-hidden="true" />
            All tenants
            <span className="ml-auto text-xs text-inkMuted">{tenants.length}</span>
          </Link>
        )}
        {visible.length === 0 && (
          <p className="px-4 py-2 text-xs text-inkMuted">No tenants match.</p>
        )}
        <ul>
          {visible.map((t) => {
            const open = t.tenantId === selectedTenantId;
            const tActive = open && !selectedLocationId;
            const loaded = !!loadedTenants[t.tenantId];
            return (
              <li key={t.tenantId} className="mx-2">
                <Link
                  to={tenantPath(t.tenantId)}
                  onClick={onNavigate}
                  aria-current={tActive ? 'page' : undefined}
                  aria-expanded={open}
                  title={t.tenantName}
                  data-testid={`tree-tenant-${t.tenantId}`}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg no-underline ${
                    tActive ? 'bg-navActiveBg text-navActive font-bold' : 'text-ink hover:bg-cream-dark font-semibold'
                  }`}
                >
                  <span className="text-inkMuted" aria-hidden="true">
                    {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </span>
                  {showClientStatus && (
                    <span
                      className={`w-1.5 h-1.5 rounded-full shrink-0 ${t.isLive ? 'bg-success' : 'bg-inkMuted/40'}`}
                      title={t.isLive ? 'Live client' : 'Not live'}
                      data-testid={`tree-live-dot-${t.tenantId}`}
                    />
                  )}
                  <span className="truncate">{t.tenantName}</span>
                  <span className="ml-auto text-xs text-inkMuted font-semibold" data-testid={`tree-count-${t.tenantId}`}>
                    {loaded ? t.locations.length : ''}
                  </span>
                </Link>
                {open && (
                  <div className="ml-5 border-l border-softBorder pl-2 my-0.5">
                    {!loaded ? (
                      <p className="px-2 py-1 text-xs text-inkMuted">
                        {loadingTenants[t.tenantId] ? 'Loading...' : ''}
                      </p>
                    ) : t.locations.length === 0 ? (
                      <p className="px-2 py-1 text-xs text-inkMuted">No locations found.</p>
                    ) : (
                      <ul>
                        {t.locations.map((l) => {
                          const lActive = l.id === selectedLocationId;
                          return (
                            <li key={l.id}>
                              <Link
                                to={locationPath(t.tenantId, l.id)}
                                onClick={onNavigate}
                                aria-current={lActive ? 'page' : undefined}
                                title={l.name}
                                data-testid={`tree-location-${l.id}`}
                                className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[13px] no-underline ${
                                  lActive ? 'bg-navActiveBg text-navActive font-bold' : 'text-ink hover:bg-cream-dark'
                                }`}
                              >
                                <MapPin className="w-3.5 h-3.5 text-inkMuted shrink-0" aria-hidden="true" />
                                <span className="truncate">{l.name}</span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {loaded && canCreateLocation(t.tenantId) && (
                      <button
                        type="button"
                        onClick={() => onAddLocation(t)}
                        className="flex items-center gap-1.5 px-2 py-1 text-[13px] font-semibold text-coral hover:underline"
                        data-testid={`tree-add-location-${t.tenantId}`}
                      >
                        <Plus className="w-3.5 h-3.5" aria-hidden="true" />
                        Add location
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
